import cx from 'classnames';
import { useCallback, useState, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  ActionType,
  EventAttributes,
  SourceType,
} from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import type { JwmLinkNavData } from '@trello/jwm';
import { canSelfJoin, executeSelfJoin } from '@trello/jwm';
import { Button } from '@trello/nachos/button';

import { useJoinJiraDetailsFragment } from './JoinJiraDetailsFragment.generated';

import * as styles from './JoinJiraButton.module.less';

interface JoinJiraButtonProps {
  workspaceId?: string;
  cloudId: string | undefined;
  navigationData: JwmLinkNavData;
  analyticsSource: SourceType;
  uiSource?: string;
  setJoined: (joined: boolean) => void;
}

const actionSubject = 'jiraLinkedWorkspaceSelfJoin';

export const joinJira = async (
  {
    workspaceId,
    cloudId,
    navigationData,
    analyticsSource,
    uiSource,
    setJoined,
  }: JoinJiraButtonProps,
  workspaceCreationMethod?: string,
) => {
  Analytics.sendClickedLinkEvent({
    linkName: 'joinJwmLink',
    source: analyticsSource,
    containers: formatContainers({ idOrganization: workspaceId }),
  });
  let joinSuccess = false;
  const canSelfJoinResult = await canSelfJoin(cloudId);
  let attributes: EventAttributes, action: ActionType;
  if (canSelfJoinResult.result) {
    try {
      const joinResult = await executeSelfJoin(cloudId);
      joinSuccess = joinResult.success;
      action = joinSuccess ? 'succeeded' : 'failed';
      attributes = {
        selfJoinAttemtped: true,
        selfJoinResult: joinResult.result,
        selfJoinLatency: joinResult.time,
        iisCapabilitiesHasDirectAccess: canSelfJoinResult.hasDirectAccess,
        iisCapabilitiesUserAccessLevel: canSelfJoinResult.userAccessLevel,
        iisCapabilitiesVerificationStatus: canSelfJoinResult.verificationStatus,
      };
    } catch (error) {
      action = 'failed';
      attributes = {
        selfJoinAttemtped: true,
        selfJoinResult: 'JOIN_ERROR',
        selfJoinLatency: 0,
        iisCapabilitiesHasDirectAccess: canSelfJoinResult.hasDirectAccess,
        iisCapabilitiesUserAccessLevel: canSelfJoinResult.userAccessLevel,
        iisCapabilitiesVerificationStatus: canSelfJoinResult.verificationStatus,
      };
    }
  } else {
    action = 'skipped';
    attributes = {
      selfJoinAttemtped: false,
      selfJoinResult: 'NO_OP',
      selfJoinLatency: 0,
      iisCapabilitiesHasDirectAccess: canSelfJoinResult.hasDirectAccess,
      iisCapabilitiesUserAccessLevel: canSelfJoinResult.userAccessLevel,
      iisCapabilitiesVerificationStatus: canSelfJoinResult.verificationStatus,
    };
  }
  let newWin;
  if (joinSuccess) {
    newWin = window.open(navigationData.jwmProjectsUrl, '_blank');
    setJoined(true);
  } else {
    newWin = window.open(navigationData.jwmSiteUrl, '_blank');
  }
  // Citation: https://stackoverflow.com/questions/2914/how-can-i-detect-if-a-browser-is-blocking-a-popup
  const popupOpened = !(
    !newWin ||
    newWin.closed ||
    typeof newWin.closed === 'undefined'
  );
  attributes.popupOpened = popupOpened;
  attributes.workspaceCreationMethod = workspaceCreationMethod || 'undefined';
  attributes.uiSource = uiSource || 'join-jira-button';
  Analytics.sendOperationalEvent({
    action,
    actionSubject,
    source: analyticsSource,
    attributes,
  });
};

export const JoinJiraButton: FunctionComponent<JoinJiraButtonProps> = ({
  workspaceId,
  cloudId,
  navigationData,
  analyticsSource,
  uiSource,
  setJoined,
}) => {
  const [joinLoading, setJoinLoading] = useState(false);

  const { data } = useJoinJiraDetailsFragment({
    from: { id: workspaceId },
  });

  const onClickJoin = useCallback(async () => {
    setJoinLoading(true);
    await joinJira(
      {
        workspaceId,
        cloudId,
        navigationData,
        analyticsSource,
        uiSource,
        setJoined,
      },
      data?.creationMethod || undefined,
    );
    setJoinLoading(false);
  }, [
    workspaceId,
    cloudId,
    navigationData,
    analyticsSource,
    uiSource,
    setJoined,
    data?.creationMethod,
  ]);

  return (
    <>
      {/* note: we're intentionally using the Nachos Button with custom dynamic styles & colors
    instead of the DynamicButton component */}
      <Button
        isLoading={joinLoading}
        className={cx(
          analyticsSource === 'memberHomeWorkspaceSidebarSection'
            ? styles.joinButtonHomePage
            : styles.joinButton,
        )}
        onClick={onClickJoin}
      >
        <FormattedMessage
          id="templates.jwm_site_workspace_link.join"
          defaultMessage="Join"
          description="Button to join linked Jira site"
        />
      </Button>
    </>
  );
};
